import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ScheduleType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import { Link } from '@/components/ui/link/link.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { NextAppointment } from '@/services/appointment/get-next-appointments.service';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { AppointmentMode } from '@/types/enums/appointments.enum';
import { resolveAppointmentEntity } from '@/utils/formatters/appointment-entity';

interface NextAppointmentsSectionCardProps {
  appointment: NextAppointment;
}

interface CardEntity {
  name: string;
  avatar?: string;
  type: EntityTypeEnum;
}

const TIMEZONE = 'America/Sao_Paulo';

function resolveCardEntity(appointment: NextAppointment): CardEntity | null {
  const entity = resolveAppointmentEntity(appointment);

  if (!entity) {
    return null;
  }

  return {
    name: entity.name,
    avatar: entity.avatar,
    type: entity.isProfessional ? EntityTypeEnum.PROFESSIONAL : EntityTypeEnum.CLINIC,
  };
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatTime(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIMEZONE,
  });
}

function formatAppointmentDate(isoDate: string, isoEnd?: string | null): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const weekday = date
    .toLocaleDateString('pt-BR', { weekday: 'short', timeZone: TIMEZONE })
    .replace('.', '');
  const day = date.toLocaleDateString('pt-BR', { day: 'numeric', timeZone: TIMEZONE });
  const month = date
    .toLocaleDateString('pt-BR', { month: 'short', timeZone: TIMEZONE })
    .replace('.', '');
  const startTime = formatTime(isoDate);
  const endTime = isoEnd ? formatTime(isoEnd) : '';

  const time = endTime ? `${startTime} às ${endTime}` : startTime;

  return `${capitalize(weekday)}, ${day} ${month} • ${time}`;
}

function resolveHeader(appointment: NextAppointment): { text: string; isPending: boolean } {
  if (appointment.mode === AppointmentMode.STANDALONE) {
    return { text: 'Guia Avulsa • Combine data e horário', isPending: true };
  }

  if (appointment.scheduleType === ScheduleType.FLEX || !appointment.date) {
    return { text: 'Agenda Flex • Combine data e horário', isPending: true };
  }

  return { text: formatAppointmentDate(appointment.date, appointment.slotEnd), isPending: false };
}

function resolveProcedureLabel(items: NextAppointment['items']): string | undefined {
  if (items.length > 1) {
    return `${items.length} exames (Consulte a lista)`;
  }

  return items[0]?.procedure.name;
}

export function NextAppointmentsSectionCard({ appointment }: NextAppointmentsSectionCardProps) {
  const entity = resolveCardEntity(appointment);

  if (!entity) {
    return null;
  }

  const procedureLabel = resolveProcedureLabel(appointment.items);
  const { text: headerText, isPending } = resolveHeader(appointment);

  return (
    <Link
      prefetch={false}
      href={`/agendamentos/${appointment.id}`}
      data-testid={DataTestIdEnum.HOME_NEXT_APPOINTMENTS_CARD}
      className="flex flex-col gap-4 p-4 rounded-2xl bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
    >
      <Typography
        variant={TypographyVariant.BODY_TEXT}
        className={`font-bold ${isPending ? 'text-warning-700' : 'text-green-500'}`}
      >
        {headerText}
      </Typography>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center bg-gray-100 rounded-lg w-14 h-14 shrink-0">
            <EntityAvatar
              src={entity.avatar}
              alt={entity.name}
              type={entity.type}
              size="md"
              width={48}
              height={48}
              shouldAddLightbox={false}
            />
          </div>
          <Typography
            as="h3"
            variant={TypographyVariant.BODY_TEXT}
            className="flex-1 min-w-0 font-bold text-gray-700 line-clamp-2"
          >
            {entity.name}
          </Typography>
        </div>
        {procedureLabel && (
          <Typography
            variant={TypographyVariant.BODY_TEXT}
            className="text-gray-700 line-clamp-1"
          >
            {procedureLabel}
          </Typography>
        )}
      </div>
    </Link>
  );
}
